import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Api from '../../../api';
import DentistNavbar from '../../../components/DentistNavbar';
import TalentBackButton from '../../../components/TalentBackButton/TalentBackButton';
import TalentNavbar from '../../../components/TalentNavbar';
import TalentPageLayout from '../../../components/TalentPageLayout';
import { usePersistedState } from '../../../hooks';
import { entities } from '../../../reduxStore';
import { HYGIENIST_TYPES, UserAccountProps } from '../../../utils/types';
import * as S from './MixpanelLogs.styles';

interface LogMeta {
  total: number;
  filtered: number;
  limit: number;
  grep: string | null;
  sinceMinutes: number | null;
}

/**
 * MixpanelLogs - QA tool for viewing Mixpanel audit logs
 * Only accessible to test accounts (isTest=1)
 */
export default function MixpanelLogs() {
  const history = useHistory();
  const persistedUser = usePersistedState<UserAccountProps>({
    entityName: entities.user,
  })[0];
  const [lines, setLines] = useState<string[]>([]);
  const [meta, setMeta] = useState<LogMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [limit, setLimit] = useState(300);
  const [grep, setGrep] = useState('');
  const [sinceMinutes, setSinceMinutes] = useState<number | ''>('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const tempStarsUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const currentUser = (persistedUser?.dentistId || persistedUser?.hygienistId ? persistedUser : tempStarsUser) || {};
  const isDentist = Number.isInteger(currentUser?.dentistId);
  const hygienist = currentUser?.hygienist;
  const isHygOrAsst = [HYGIENIST_TYPES.HYGIENIST, HYGIENIST_TYPES.ASSISTANT].includes(Number(hygienist?.type) as any);

  const handleBack = useCallback(() => {
    const goStartingPage = (window as any)?.TempStars?.App?.gotoStartingPage;
    if (typeof goStartingPage === 'function') {
      goStartingPage();
      return;
    }

    history.push({ pathname: isDentist ? 'dentistHome' : isHygOrAsst ? 'hygienistHome' : 'jobPostsList' });
  }, [history, isDentist, isHygOrAsst]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('sinceMinutes', String(sinceMinutes ? Number(sinceMinutes) : 60));
      
      // Use live endpoint that queries Mixpanel API directly
      // This works in staging/production without file dependencies
      const result = await Api.getMixpanelLogsLive(params.toString());
      
      // Apply grep filter on client side if provided
      let filteredLines = result.lines;
      if (grep) {
        filteredLines = result.lines.filter((line: string) => 
          line.toLowerCase().includes(grep.toLowerCase())
        );
      }
      
      setLines(filteredLines);
      setMeta({
        ...result.meta,
        filtered: filteredLines.length,
        grep: grep || null
      });
    } catch (err: any) {
      // Provide helpful error messages based on error type
      const errorMsg = err.message || String(err);
      
      if (errorMsg.includes('503') || errorMsg.includes('not enabled')) {
        setError(
          '⚙️ Audit service is not enabled. ' +
          'Backend admin needs to set MIXPANEL_AUDIT_ENABLED=true in .env file. ' +
          'See T2-4514 for setup instructions.'
        );
      } else if (errorMsg.includes('401') || errorMsg.includes('Authentication')) {
        setError(
          '🔒 Authentication required. Please log in as a test account to access audit logs.'
        );
      } else if (errorMsg.includes('403') || errorMsg.includes('Access denied')) {
        setError(
          '🚫 Access denied. This page is only available to test accounts (isTest=1). ' +
          'Contact admin to set your account as a test account.'
        );
      } else if (errorMsg.includes('404') || errorMsg.includes('Not found')) {
        setError(
          '🌍 This feature is only available in development, staging, and test environments. ' +
          'Current environment does not allow audit access.'
        );
      } else if (errorMsg.includes('MIXPANEL_API_SECRET') || errorMsg.includes('not configured')) {
        setError(
          '🔑 Mixpanel credentials not configured. ' +
          'Backend admin needs to set MIXPANEL_API_SECRET and MIXPANEL_PROJECT_ID in .env file.'
        );
      } else if (errorMsg.includes('Rate limit')) {
        setError(
          '⏱️ Rate limit exceeded. Please wait a moment before refreshing again.'
        );
      } else {
        setError(`Failed to fetch logs: ${errorMsg}`);
      }
      
      console.error('Error fetching Mixpanel logs:', err);
    } finally {
      setLoading(false);
    }
  }, [limit, grep, sinceMinutes]);

  // Initial load
  useEffect(() => {
    fetchLogs();
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchLogs();
    }, 10000); // 10 seconds
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  // Highlight PASS/FAIL/CRITICAL in logs
  const renderLine = (line: string, index: number) => {
    const isPass = line.includes('PASS');
    const isFail = line.includes('FAIL');
    const isCritical = line.includes('CRITICAL');
    
    return (
      <S.LogLine key={index} isPass={isPass} isFail={isFail} isCritical={isCritical}>
        {line}
      </S.LogLine>
    );
  };

  const logsBody = (
    <>
      <TalentBackButton onBack={handleBack} />
      <S.Container>
        <S.Header>
          <h1>Mixpanel Audit Logs</h1>
          <S.Subtitle>
            Real-time validation of Mixpanel tracking events (test accounts only)
          </S.Subtitle>
        </S.Header>

        <S.Controls>
          <S.ControlRow>
            <label>
              Limit:
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={300}>300</option>
                <option value={1000}>1000</option>
              </select>
            </label>

            <label>
              Filter:
              <input
                type="text"
                value={grep}
                onChange={(e) => setGrep(e.target.value)}
                placeholder="search logs..."
              />
            </label>

            <label>
              Since (min):
              <input
                type="number"
                value={sinceMinutes}
                onChange={(e) => setSinceMinutes(e.target.value ? Number(e.target.value) : '')}
                placeholder="60"
                title="Time window in minutes (try 1440 for last 24 hours, 10080 for last 7 days)"
                aria-label="Time window in minutes"
              />
            </label>
          </S.ControlRow>

          <S.ControlRow>
            <button onClick={fetchLogs} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>

            <S.CheckboxLabel>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                title="Enable auto-refresh every 10 seconds"
                aria-label="Enable auto-refresh every 10 seconds"
              />
              Auto-refresh (10s)
            </S.CheckboxLabel>
            
            <S.StatusButton 
              onClick={async () => {
                try {
                  const status = await Api.getMixpanelAuditStatus();
                  const displayText = `Configuration Status:\n\n` +
                    `✅ Configured: ${status.configured ? 'YES' : 'NO'}\n\n` +
                    `Details:\n` +
                    `  API Secret: ${status.details.apiSecret}\n` +
                    `  Project ID: ${status.details.projectId}\n` +
                    `  Audit Enabled: ${status.details.auditEnabled}\n` +
                    `  Environment: ${status.details.environment}\n` +
                    `  Env Allowed: ${status.details.environmentAllowed}\n\n` +
                    (status.recommendations.length > 0 
                      ? `Recommendations:\n${status.recommendations.map((r: string) => `  • ${r}`).join('\n')}`
                      : '✅ No issues found!');
                  alert(displayText);
                } catch (err) {
                  const errorMsg = (err as Error).message || String(err);
                  if (errorMsg.includes('403') || errorMsg.includes('Access denied')) {
                    alert('Access Denied\n\nYou must be logged in as a test account (isTest=1) to check status.');
                  } else {
                    alert('Failed to check status: ' + errorMsg + '\n\nMake sure:\n- API server is running\n- You are logged in\n- Your account is a test account');
                  }
                }
              }}
            >
              Check Status
            </S.StatusButton>
          </S.ControlRow>
        </S.Controls>

        {meta && (
          <S.Meta>
            Showing {meta.filtered} of {meta.total} lines
            {meta.grep && ` (filtered by "${meta.grep}")`}
            {meta.sinceMinutes && ` (last ${meta.sinceMinutes} minutes)`}
          </S.Meta>
        )}

        {error && (
          <S.Error>
            ⚠️ {error}
          </S.Error>
        )}

        <S.LogsContainer>
          {lines.length === 0 && !loading && (
            <S.Empty>
              No audit events found in the selected time window.
              <br />
              <br />
              Try expanding the time window or perform actions in the app to generate tracking events.
              <br />
              Events are queried live from Mixpanel and validated in real-time.
            </S.Empty>
          )}
          {lines.map(renderLine)}
        </S.LogsContainer>
      </S.Container>
    </>
  );

  return (
    <S.Page>
      {isDentist ? (
        <>
          <DentistNavbar
            title="Mixpanel Logs"
            showGetTempButton={false}
          />
          {logsBody}
        </>
      ) : (
        <TalentPageLayout
          navbar={(
            <TalentNavbar
              firstName={hygienist?.firstName || ''}
              lastName={hygienist?.lastName || ''}
              photoUrl={hygienist?.photoUrl || undefined}
              isHygOrAsst={isHygOrAsst}
              showAddShiftButton={false}
              showJobBoardButton={false}
              pageTitle="Mixpanel Logs"
            />
          )}
        >
          {logsBody}
        </TalentPageLayout>
      )}
    </S.Page>
  );
}


